import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import QuillResizeImage from "quill-resize-image";
import DOMPurify from "dompurify";
import { Loader2, Plus, Save, Trash2, X, Image as ImageIcon, Search, Pin, CheckSquare, AlertCircle, Flame, Eye, EyeOff, User as UserIcon, Calendar as CalendarIcon, Edit3, ChevronsLeft, ChevronsRight } from "lucide-react";
import { adminApi, api } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";
import BadgePicker from "../BadgePicker";

// Quill 2.x: Resize-Modul registrieren (Bild kann per Drag-Handles skaliert werden)
try {
  Quill.register("modules/resize", QuillResizeImage);
} catch (e) { /* schon registriert */ }

// Custom Image-Blot: preserves `style` (float, margin, display) und `class`,
// damit Alignment (links/rechts/zentriert) NICHT beim Re-Parse verloren geht.
try {
  const BaseImage = Quill.import("formats/image");
  const ATTRS = ["src", "alt", "width", "height", "style", "class"];
  class HabthekImage extends BaseImage {
    static formats(node) {
      return ATTRS.reduce((fmts, attr) => {
        if (node.hasAttribute(attr)) fmts[attr] = node.getAttribute(attr);
        return fmts;
      }, {});
    }
    format(name, value) {
      if (ATTRS.indexOf(name) !== -1) {
        if (value) this.domNode.setAttribute(name, value);
        else this.domNode.removeAttribute(name);
      } else {
        super.format(name, value);
      }
    }
  }
  HabthekImage.blotName = "image";
  HabthekImage.tagName = "IMG";
  Quill.register(HabthekImage, true);
} catch (e) { /* schon registriert */ }

// Custom Blot: Trennlinie (<hr>) als Block-Embed
try {
  const BlockEmbed = Quill.import("blots/block/embed");
  class DividerBlot extends BlockEmbed {}
  DividerBlot.blotName = "divider";
  DividerBlot.tagName = "hr";
  Quill.register(DividerBlot, true);
} catch (e) { /* schon registriert */ }

// Custom Blot: News-Galerie (<div class="news-gallery"> mit <img>-Children)
// Wichtig, weil Quill 2.x sonst <div>-Wrapper beim Paste entfernt.
try {
  const BlockEmbed = Quill.import("blots/block/embed");
  class GalleryBlot extends BlockEmbed {
    static create(urls) {
      const node = super.create();
      node.setAttribute("class", "news-gallery");
      node.setAttribute("data-gallery", "true");
      const arr = Array.isArray(urls) ? urls : (urls || "").split("||").filter(Boolean);
      arr.forEach((u) => {
        const img = document.createElement("img");
        img.setAttribute("src", u);
        img.setAttribute("alt", "Galerie-Bild");
        img.setAttribute("loading", "lazy");
        node.appendChild(img);
      });
      return node;
    }
    static value(node) {
      return Array.from(node.querySelectorAll("img"))
        .map((im) => im.getAttribute("src"))
        .filter(Boolean)
        .join("||");
    }
  }
  GalleryBlot.blotName = "newsGallery";
  GalleryBlot.tagName = "div";
  GalleryBlot.className = "news-gallery";
  Quill.register(GalleryBlot, true);
} catch (e) { /* schon registriert */ }

const IMG_URL_RE = /^(https?:\/\/\S+\.(?:png|jpe?g|gif|webp|bmp|avif|svg))(?:\?[^\s]*)?$/i;

const DEFAULT_CATEGORIES = ["News", "Glitch", "Retro", "Community", "Jubiläum", "Rare", "Event"];

// Identische Logik wie auf der oeffentlichen ArticleDetail-Seite
const categoryColor = (c) => {
  switch (c) {
    case "Jubiläum": return "bg-[#ff9800]/15 text-[#ffb84d] border-[#ff9800]/30";
    case "News":     return "bg-[#7c4dff]/15 text-[#b39bff] border-[#7c4dff]/30";
    case "Retro":    return "bg-emerald-500/10 text-emerald-300 border-emerald-500/30";
    case "Glitch":   return "bg-rose-500/10 text-rose-300 border-rose-500/30";
    case "Community":return "bg-sky-500/10 text-sky-300 border-sky-500/30";
    case "Rare":     return "bg-amber-500/10 text-amber-300 border-amber-500/30";
    case "Event":    return "bg-pink-500/10 text-pink-300 border-pink-500/30";
    default:         return "bg-white/5 text-zinc-300 border-white/10";
  }
};

const formatDateLong = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return d; }
};

const emptyArticle = {
  id: "",
  title: "",
  excerpt: "",
  category: "News",
  author: "Habthek Team",
  date: new Date().toISOString().slice(0, 10),
  time: new Date().toTimeString().slice(0, 5),
  cover: "",
  header_image: "",
  content_html: "",
  link_in_timeline: true,
  badges: [],
};

const NewsPanel = ({ token, perms = { admin: true, news_write: true, news_review: true, news_sticky: true } }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const isFullAdmin = perms.admin;
  const { toast } = useToast();
  const [articles, setArticles] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // article object or null
  const [saving, setSaving] = useState(false);
  const [hotTopicId, setHotTopicId] = useState("");
  const [allCategories, setAllCategories] = useState(DEFAULT_CATEGORIES);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("alle"); // alle | online | offline | review
  const quillRef = useRef(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryUrls, setGalleryUrls] = useState("");
  const [galleryUploading, setGalleryUploading] = useState(false);
  // Inline-Image-Action-Popover (Klick auf <img> im Editor)
  const [imgAction, setImgAction] = useState(null); // { x, y, src, el }
  const [imgActionBusy, setImgActionBusy] = useState(false);

  // Hilfs-Insert in den Editor an aktueller Cursor-Position
  const insertHtmlAtCursor = (html) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
    editor.clipboard.dangerouslyPasteHTML(range.index, html, "user");
    editor.setSelection(range.index + 1, 0, "silent");
  };

  const insertTable = () => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const raw = window.prompt("Tabelle einfügen – Format: ZeilenxSpalten (z.B. 3x4)", "3x3");
    if (!raw) return;
    const m = raw.toLowerCase().match(/^(\d+)\s*x\s*(\d+)$/);
    if (!m) {
      toast({ title: "Ungültiges Format", description: "Bitte 'ZeilenxSpalten' angeben, z.B. 3x4.", variant: "destructive" });
      return;
    }
    const rows = Math.min(20, Math.max(1, parseInt(m[1], 10)));
    const cols = Math.min(10, Math.max(1, parseInt(m[2], 10)));
    const cellTd = `<td style="border:1px solid #d4d4d8;padding:6px 10px;min-width:60px;">&nbsp;</td>`;
    const headerTd = `<td style="border:1px solid #d4d4d8;padding:6px 10px;min-width:60px;background:#f4f4f5;font-weight:700;">&nbsp;</td>`;
    const headerRow = `<tr>${headerTd.repeat(cols)}</tr>`;
    const bodyRows = Array.from({ length: rows - 1 }, () => `<tr>${cellTd.repeat(cols)}</tr>`).join("");
    const html = `<table class="news-table" style="border-collapse:collapse;width:100%;margin:12px 0;">${headerRow}${bodyRows}</table><p><br/></p>`;
    insertHtmlAtCursor(html);
  };

  const insertDivider = () => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
    editor.insertEmbed(range.index, "divider", true, "user");
    editor.insertText(range.index + 1, "\n", "user");
    editor.setSelection(range.index + 2, 0, "silent");
  };

  const openGallery = () => {
    setGalleryUrls("");
    setGalleryOpen(true);
  };

  // Iter51c: Klick auf Bild -> KEIN Popover mehr, damit quill-resize-image
  // (Links/Rechts/Mitte/Skalieren) frei funktioniert. Rechts-Klick zeigt weiterhin
  // das Aktions-Popover (Vergroessern / Aus Artikel entfernen / Aus Galerie loeschen).
  useEffect(() => {
    if (!editing) return undefined;
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return undefined;
    const root = editor.root;
    const onContext = (ev) => {
      const t = ev.target;
      if (t && t.tagName === "IMG") {
        ev.preventDefault();
        const rect = t.getBoundingClientRect();
        setImgAction({
          x: rect.left + window.scrollX + rect.width / 2,
          y: rect.top + window.scrollY,
          src: t.getAttribute("src") || "",
          el: t,
        });
      }
    };
    root.addEventListener("contextmenu", onContext);
    return () => root.removeEventListener("contextmenu", onContext);
  }, [editing?.id, editing?.content_html ? "with" : "empty"]);

  // Entfernt ein Bild aus dem Editor (Quill) und gibt das resultierende HTML zurueck
  const removeImageFromEditor = (imgEl) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor || !imgEl) return;
    // Versuche, das Embed via Quill-API zu loeschen (sauberer)
    const blot = Quill.find(imgEl);
    if (blot && typeof blot.deleteAt === "function") {
      const index = editor.getIndex(blot);
      editor.deleteText(index, 1, "user");
    } else {
      // Fallback: DOM entfernen und content_html neu setzen
      imgEl.parentNode?.removeChild(imgEl);
      setEditing((e) => ({ ...e, content_html: editor.root.innerHTML }));
    }
  };

  // Soft-Delete im Bucket fuer /api/uploads/<id>.<ext> URLs
  const deleteFromMediaLibrary = async (src, force) => {
    try {
      const m = (src || "").match(/\/api\/uploads\/([^/?#]+)/);
      if (!m) return false;
      await a.deleteUpload(m[1], force);
      return true;
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.used_in) {
        const titles = detail.used_in.map((u) => `• ${u.title}`).join("\n");
        const force2 = window.confirm(
          `Dieses Bild wird in ${detail.used_in.length} weiteren Artikel(n) verwendet:\n\n${titles}\n\nTrotzdem aus der Galerie loeschen?`
        );
        if (force2) return await deleteFromMediaLibrary(src, true);
        return false;
      }
      throw err;
    }
  };

  const insertGalleryFromUrls = (urls) => {
    const clean = urls.map((u) => (u || "").trim()).filter(Boolean);
    if (clean.length < 1) {
      toast({ title: "Mindestens 1 Bild", description: "Bitte mindestens eine Bild-URL angeben.", variant: "destructive" });
      return;
    }
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
    // BlockEmbed nutzt pipe-separated string als Value (Quill serialisiert nur Strings sauber)
    editor.insertEmbed(range.index, "newsGallery", clean.slice(0, 6).join("||"), "user");
    editor.insertText(range.index + 1, "\n", "user");
    editor.setSelection(range.index + 2, 0, "silent");
    setGalleryOpen(false);
  };

  const onGalleryUpload = async (filesList) => {
    const files = Array.from(filesList || []);
    if (files.length === 0) return;
    setGalleryUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await a.uploadImages(fd);
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const absolute = (r.urls || []).map((u) => (u.startsWith("http") ? u : `${apiBase}${u}`));
      const newList = (galleryUrls ? galleryUrls + "\n" : "") + absolute.join("\n");
      setGalleryUrls(newList);
      toast({ title: `${absolute.length} Bild${absolute.length === 1 ? "" : "er"} hochgeladen` });
    } catch (err) {
      toast({ title: "Upload-Fehler", description: err?.response?.data?.detail || String(err), variant: "destructive" });
    } finally {
      setGalleryUploading(false);
    }
  };

  // Eigener Link-Handler: erkennt Bild-URLs und fügt sie als <img> statt als <a> ein
  const handleLink = (value) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    if (!value) {
      editor.format("link", false);
      return;
    }
    const range = editor.getSelection(true);
    const selectedText = range && range.length > 0 ? editor.getText(range.index, range.length).trim() : "";
    const url = window.prompt("URL eingeben:", selectedText && /^https?:\/\//i.test(selectedText) ? selectedText : "https://");
    if (!url) return;
    const trimmed = url.trim();
    if (IMG_URL_RE.test(trimmed)) {
      // Statt Link -> Bild einfuegen
      const insertAt = range ? range.index : editor.getLength();
      if (range && range.length > 0) editor.deleteText(range.index, range.length, "user");
      editor.insertEmbed(insertAt, "image", trimmed, "user");
      editor.setSelection(insertAt + 1, 0, "silent");
      return;
    }
    if (range && range.length > 0) {
      editor.format("link", trimmed, "user");
    } else {
      // kein selected Text: URL als Linktext einfuegen
      editor.insertText(range ? range.index : editor.getLength(), trimmed, { link: trimmed }, "user");
    }
  };

  const QUILL_MODULES = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ color: [] }, { background: [] }],
        ["link", "image", "blockquote", "code-block"],
        ["table", "divider", "gallery"],
        [{ align: [] }],
        ["clean"],
      ],
      handlers: {
        table: insertTable,
        divider: insertDivider,
        gallery: openGallery,
        link: handleLink,
      },
    },
    clipboard: { matchVisual: false },
    resize: {
      locale: {
        altTip: "Halte ALT zum Skalieren ohne Seitenverhältnis",
        floatLeft: "Links",
        floatRight: "Rechts",
        center: "Mitte",
        restore: "Original",
      },
    },
  }), []);

  // Paste-Handler: reine Bild-URL Pastes -> <img> direkt im Editor
  useEffect(() => {
    if (!editing) return;
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const root = editor.root;
    const onPaste = (e) => {
      const text = e.clipboardData?.getData("text/plain")?.trim();
      if (!text || !IMG_URL_RE.test(text)) return;
      const html = e.clipboardData?.getData("text/html");
      if (html && html.length > 0) return;
      e.preventDefault();
      const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
      if (range.length) editor.deleteText(range.index, range.length, "user");
      editor.insertEmbed(range.index, "image", text, "user");
      editor.setSelection(range.index + 1, 0, "silent");
    };
    root.addEventListener("paste", onPaste);
    return () => root.removeEventListener("paste", onPaste);
  }, [editing?.id]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      // Immer cmsListArticles nehmen, weil der oeffentliche /articles Endpunkt
      // offline-Artikel filtert. Admins muessen aber auch Offline-/Draft-Artikel
      // sehen koennen, um sie wieder online zu stellen.
      const [list, hdrs, settings] = await Promise.all([
        a.cmsListArticles(),
        api.listHeaders(),
        api.getSettings(),
      ]);
      setArticles(list);
      setHeaders(hdrs);
      setHotTopicId(settings.hot_topic_article_id || "");
      // Dynamische Kategorien aus den Settings laden, fallback auf Defaults.
      // Aktive UND ausgeblendete Kategorien duerfen im Admin gewaehlt werden,
      // damit man bestehende Artikel weiter editieren kann.
      const cats = Array.isArray(settings.news_categories) && settings.news_categories.length > 0
        ? settings.news_categories
        : DEFAULT_CATEGORIES;
      setAllCategories(cats);
    } finally {
      setLoading(false);
    }
  };

  const onSetHotTopic = async (art) => {
    const isCurrent = hotTopicId === art.id;
    const broadcast = (article_id) => {
      try {
        if (typeof window !== "undefined" && "BroadcastChannel" in window) {
          const ch = new BroadcastChannel("habbothek-hot-topic");
          ch.postMessage({ type: "hot-topic-changed", article_id });
          ch.close();
        }
      } catch { /* ignore */ }
    };
    if (isCurrent) {
      if (!window.confirm("TOP News entfernen?")) return;
      try {
        await a.setHotTopic("", null);
        setHotTopicId("");
        broadcast("");
        toast({ title: "TOP News entfernt" });
      } catch (err) {
        toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
      }
    } else {
      if (!window.confirm(`"${art.title}" als TOP News setzen?\n(Erscheint als roter Banner oben auf der Seite – live in allen offenen Tabs)`)) return;
      try {
        await a.setHotTopic(art.id, null);
        setHotTopicId(art.id);
        broadcast(art.id);
        toast({ title: "TOP News gesetzt", description: "Alle offenen Tabs sehen den Banner sofort." });
      } catch (err) {
        toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
      }
    }
  };

  useEffect(() => { load();  }, []);

  const filtered = articles.filter((x) => {
    if (q.trim() && !x.title.toLowerCase().includes(q.toLowerCase())) return false;
    if (filterStatus === "online" && (x.published === false || x.needs_review)) return false;
    if (filterStatus === "offline" && !(x.published === false && !x.needs_review)) return false;
    if (filterStatus === "review" && !x.needs_review) return false;
    return true;
  });

  // Counters fuer die Filter-Tabs
  const counts = {
    alle: articles.length,
    online: articles.filter((a) => a.published !== false && !a.needs_review).length,
    offline: articles.filter((a) => a.published === false && !a.needs_review).length,
    review: articles.filter((a) => a.needs_review).length,
  };

  const startNew = () => setEditing({ ...emptyArticle });
  const startEdit = (art) => setEditing({ ...emptyArticle, ...art });

  const onSave = async () => {
    if (!editing.title.trim()) return toast({ title: "Titel fehlt", variant: "destructive" });
    setSaving(true);
    try {
      const payload = { ...editing };
      const isUpdate = !!editing.created_at;
      if (isFullAdmin) {
        if (isUpdate) await a.updateArticle(editing.id, payload);
        else await a.createArticle(payload);
      } else {
        if (isUpdate) await a.cmsUpdateArticle(editing.id, payload);
        else await a.cmsCreateArticle(payload);
      }
      toast({ title: "Artikel gespeichert" });
      setEditing(null);
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail || "Fehler beim Speichern", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (art) => {
    if (!window.confirm(`Artikel "${art.title}" wirklich löschen?`)) return;
    try {
      if (isFullAdmin) await a.deleteArticle(art.id);
      else await a.cmsDeleteArticle(art.id);
      toast({ title: "Gelöscht" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", variant: "destructive" });
    }
  };

  const onApprove = async (art) => {
    try {
      await a.cmsApprove(art.id);
      toast({ title: "Artikel freigegeben" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  const onTogglePublish = async (art) => {
    const next = !(art.published === false);  // wenn aktuell online -> offline
    try {
      await a.togglePublishArticle(art.id, !next);  // setze auf das Gegenteil von "aktuell online"
      toast({ title: next ? "Offline genommen" : "Online gestellt" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  const onToggleSticky = async (art) => {
    try {
      await a.cmsToggleSticky(art.id, !art.sticky);
      toast({ title: art.sticky ? "Sticky entfernt" : "Sticky gesetzt" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", variant: "destructive" });
    }
  };

  const galleryModal = galleryOpen ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={() => setGalleryOpen(false)}
      data-testid="gallery-modal"
    >
      <div
        className="w-full max-w-2xl rounded-xl border border-white/15 bg-[#10141b] p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-white">Galerie einfügen</h3>
            <p className="text-xs text-zinc-400">1–6 Bilder als Grid, klickbar mit Lightbox.</p>
          </div>
          <button
            type="button"
            onClick={() => setGalleryOpen(false)}
            className="rounded p-1 text-zinc-500 hover:bg-white/5 hover:text-white"
            data-testid="gallery-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-4">
          <label className="text-xs uppercase tracking-wider text-zinc-500">
            Bild-URLs (eine pro Zeile)
          </label>
          <Textarea
            value={galleryUrls}
            onChange={(e) => setGalleryUrls(e.target.value)}
            rows={6}
            placeholder={"https://images.habbo.com/c_images/...\nhttps://i.imgur.com/...\n/api/uploads/..."}
            className="mt-1 bg-[#0f141b] border-white/10 text-zinc-200 font-mono text-xs"
            data-testid="gallery-urls-textarea"
          />
          <div className="mt-1 text-[11px] text-zinc-500">
            Tipp: Habbo-Asset-URLs (z.B. <code>images.habbo.com/c_images/album1584/...</code>) funktionieren direkt.
          </div>
        </div>
        <div className="mt-4">
          <label className="text-xs uppercase tracking-wider text-zinc-500">
            Oder Bilder hochladen
          </label>
          <div className="mt-1 flex items-center gap-3">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => onGalleryUpload(e.target.files)}
              className="block w-full text-xs text-zinc-300 file:mr-3 file:rounded-md file:border-0 file:bg-[#7c4dff] file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-[#9d7bff] disabled:opacity-50"
              disabled={galleryUploading}
              data-testid="gallery-file-input"
            />
            {galleryUploading && <Loader2 className="h-4 w-4 animate-spin text-[#b39bff]" />}
          </div>
          <div className="mt-1 text-[11px] text-zinc-500">
            Max 8MB pro Bild, max 6 auf einmal. Werden in <code>/api/uploads</code> gespeichert.
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <Button
            onClick={() => setGalleryOpen(false)}
            variant="ghost"
            className="text-zinc-300 hover:text-white"
            data-testid="gallery-cancel"
          >
            Abbrechen
          </Button>
          <Button
            onClick={() => insertGalleryFromUrls(galleryUrls.split("\n"))}
            className="btn-primary"
            data-testid="gallery-insert"
          >
            <ImageIcon className="mr-1 h-4 w-4" /> Galerie einfügen
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  // Vorschau-Modal: rendert den Draft exakt wie auf der oeffentlichen Detail-Seite
  const previewModal = previewOpen && editing ? (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/85 p-3 sm:p-6"
      onClick={() => setPreviewOpen(false)}
      data-testid="preview-modal"
    >
      <div
        className="relative my-4 w-full max-w-5xl rounded-2xl border border-white/10 bg-[#0b0e13] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky Toolbar */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-t-2xl border-b border-white/10 bg-[#0b0e13]/95 px-4 py-3 backdrop-blur">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#b39bff]">
            <Eye className="h-3.5 w-3.5" /> Vorschau · so sehen Leser deinen Artikel
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-[10px] text-zinc-500">
              {editing.created_at && editing.published === false ? "Aktuell OFFLINE" :
                editing.created_at ? "Aktuell online" : "Noch nicht gespeichert"}
            </span>
            <Button
              onClick={() => setPreviewOpen(false)}
              className="btn-ghost rounded-lg px-3 py-1.5 text-xs"
              data-testid="preview-close"
            >
              <X className="mr-1 h-3.5 w-3.5" /> Schliessen
            </Button>
          </div>
        </div>

        <article className="px-3 py-6 sm:px-6 sm:py-8">
          {/* Header-Banner */}
          <header className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0f141b]">
            {editing.header_image ? (
              <img
                src={editing.header_image}
                alt=""
                className="hb-pixel-img h-56 w-full object-cover md:h-72"
                onError={(e) => { e.currentTarget.style.opacity = "0.25"; }}
              />
            ) : (
              <div className="h-56 w-full bg-gradient-to-br from-[#1a1f29] to-[#10141b] md:h-72" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f141b] via-[#0f141b]/55 to-transparent" />
            <div className="absolute left-4 top-4">
              <span className={`inline-flex items-center rounded-md border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${categoryColor(editing.category)}`}>
                {editing.category || "News"}
              </span>
            </div>
            <div className="absolute inset-x-4 bottom-4 md:inset-x-6 md:bottom-6">
              <h1 className="text-2xl md:text-4xl font-bold leading-tight text-white drop-shadow-md break-words">
                {editing.title || "(Ohne Titel)"}
              </h1>
              {editing.excerpt && (
                <p className="mt-2 text-sm md:text-base italic leading-snug text-zinc-100/95 drop-shadow line-clamp-3 break-words max-w-[70ch]">
                  {editing.excerpt}
                </p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-zinc-300">
                <span className="inline-flex items-center gap-1"><UserIcon className="h-3.5 w-3.5" /> {editing.author || "Habthek Team"}</span>
                <span className="inline-flex items-center gap-1"><CalendarIcon className="h-3.5 w-3.5" /> {formatDateLong(editing.date)}{editing.time ? ` · ${editing.time} Uhr` : ""}</span>
              </div>
            </div>
          </header>

          {/* Body — identische Klasse wie ArticleDetail */}
          <div className="mt-5 rounded-2xl border border-white/10 bg-[#0f141b] px-5 py-7 md:px-8 md:py-9 overflow-hidden">
            {editing.content_html ? (
              <div
                lang="de"
                className="news-content mx-auto max-w-[72ch]"
                style={{ "--news-font-size": "16px" }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editing.content_html, {
                  ADD_TAGS: ["table", "thead", "tbody", "tr", "td", "th", "hr", "div"],
                  ADD_ATTR: ["target", "rel", "class", "style", "width", "height", "align", "colspan", "rowspan", "loading", "data-gallery"],
                }) }}
              />
            ) : (
              <div className="text-center text-sm text-zinc-500 py-8">
                Noch kein Inhalt. Fuelle den Editor, um eine Vorschau zu sehen.
              </div>
            )}
            {(editing.gallery_images || []).length > 0 && (
              <div className="mt-8 border-t border-white/10 pt-6">
                <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-[#b39bff]">
                  Galerie · {editing.gallery_images.length} {editing.gallery_images.length === 1 ? "Bild" : "Bilder"}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {editing.gallery_images.map((src, idx) => (
                    <div key={src + idx} className="overflow-hidden rounded-md border border-white/10 bg-[#10141b]">
                      <img src={src} alt={`Galerie ${idx + 1}`} className="hb-pixel-img h-32 w-full object-cover" loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </div>
    </div>
  ) : null;

  // Editor-Ansicht
  if (editing) {
    return (
      <div className="space-y-4">
        {galleryModal}
        {previewModal}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            {editing.created_at ? "Artikel bearbeiten" : "Neuer Artikel"}
          </h2>
          <div className="flex gap-2">
            <Button onClick={() => setEditing(null)} className="btn-ghost rounded-lg px-3 py-2 text-sm">
              <X className="mr-1 h-4 w-4" /> Abbrechen
            </Button>
            <Button
              onClick={() => setPreviewOpen(true)}
              disabled={!editing.title.trim()}
              className="btn-ghost rounded-lg px-3 py-2 text-sm border border-[#7c4dff]/40 text-[#b39bff] hover:bg-[#7c4dff]/10"
              data-testid="news-preview-btn"
              title="Wie sieht der Artikel oeffentlich aus?"
            >
              <Eye className="mr-1 h-4 w-4" /> Vorschau
            </Button>
            <Button onClick={onSave} disabled={saving} className="btn-primary rounded-lg px-4 py-2 text-sm font-semibold">
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
              Speichern
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Titel *</label>
            <Input
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              className="mt-1 bg-[#10141b] border-white/10 text-zinc-200"
              placeholder="Spannender News-Titel"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Slug (optional)</label>
            <Input
              value={editing.id}
              onChange={(e) => setEditing({ ...editing, id: e.target.value })}
              className="mt-1 bg-[#10141b] border-white/10 text-zinc-200"
              placeholder="auto-generiert wenn leer"
              disabled={!!editing.created_at}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Kategorie</label>
            <select
              value={editing.category}
              onChange={(e) => setEditing({ ...editing, category: e.target.value })}
              className="mt-1 w-full rounded-md border border-white/10 bg-[#10141b] px-3 py-2 text-sm text-zinc-200"
            >
              {allCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Datum</label>
            <Input
              type="date"
              value={editing.date}
              onChange={(e) => setEditing({ ...editing, date: e.target.value })}
              className="mt-1 bg-[#10141b] border-white/10 text-zinc-200"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Uhrzeit (optional)</label>
            <Input
              type="time"
              value={editing.time || ""}
              onChange={(e) => setEditing({ ...editing, time: e.target.value })}
              className="mt-1 bg-[#10141b] border-white/10 text-zinc-200"
              data-testid="article-time-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Autor</label>
            <Input
              value={editing.author}
              onChange={(e) => setEditing({ ...editing, author: e.target.value })}
              className="mt-1 bg-[#10141b] border-white/10 text-zinc-200"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wider text-zinc-500">Kurzbeschreibung</label>
            <Textarea
              value={editing.excerpt}
              onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
              rows={2}
              className="mt-1 bg-[#10141b] border-white/10 text-zinc-200"
              placeholder="Teaser für die Übersicht"
            />
          </div>

          {/* Header-Bild — Pflichtfeld */}
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1">
              <span className="text-rose-400">*</span> Header-Bild (URL) <span className="text-[10px] normal-case text-zinc-600">— erforderlich</span>
            </label>
            <div className="mt-1 flex gap-2">
              <Input
                value={editing.header_image}
                onChange={(e) => setEditing({ ...editing, header_image: e.target.value })}
                placeholder="z.B. https://images.habbo.com/c_images/album1584/HW_T1.gif oder eigene Banner-URL"
                className="bg-[#10141b] border-white/10 text-zinc-200"
                data-testid="news-edit-header-url"
              />
            </div>
            <div className="mt-1 text-[11px] text-zinc-500">
              Du kannst jede Bild-URL nutzen (Habbo-API-Badge, eigener Banner, externer Host).
            </div>
            {editing.header_image && (
              <div className="mt-3 overflow-hidden rounded-lg border border-white/10">
                <img
                  src={editing.header_image}
                  alt="header preview"
                  className="h-40 w-full object-cover"
                  onError={(e) => { e.currentTarget.style.opacity = "0.25"; }}
                />
              </div>
            )}
          </div>

          {/* WYSIWYG */}
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wider text-zinc-500">Inhalt</label>
            <div className="mt-1 rounded-md border border-white/10 bg-white text-zinc-900">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={editing.content_html || ""}
                onChange={(html) => setEditing({ ...editing, content_html: html })}
                modules={QUILL_MODULES}
                placeholder="Schreibe deinen News-Artikel ..."
              />
            </div>
            <p className="mt-1 text-[11px] text-zinc-500">
              Klick auf ein Bild oeffnet die Toolbar mit Links/Rechts/Mitte und Groesse. Rechts-Klick auf ein Bild: aus Artikel oder Galerie entfernen. Alternativ Bilder als Galerie ans Artikelende haengen.
            </p>
          </div>

          {/* Bilder-Galerie (Bilder am Artikelende) */}
          <div className="md:col-span-2 rounded-xl border border-white/10 bg-[#0f141b] p-4" data-testid="article-gallery-section">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-[#b39bff]" /> Bilder am Artikelende
                </h3>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Diese Bilder erscheinen als Galerie unterhalb des Artikeltexts auf der oeffentlichen Seite.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  id="gallery-upload-input"
                  className="hidden"
                  onChange={async (ev) => {
                    const files = Array.from(ev.target.files || []);
                    if (!files.length) return;
                    setGalleryUploading(true);
                    try {
                      const form = new FormData();
                      files.forEach((f) => form.append("files", f));
                      const r = await a.uploadImages(form);
                      const newUrls = r.urls || [];
                      setEditing((prev) => prev ? ({ ...prev, gallery_images: Array.from(new Set([...(prev.gallery_images || []), ...newUrls])) }) : prev);
                      toast({ title: `${newUrls.length} Bild(er) hinzugefuegt` });
                    } catch (err) {
                      toast({ title: "Upload fehlgeschlagen", description: err?.response?.data?.detail || String(err), variant: "destructive" });
                    } finally {
                      setGalleryUploading(false);
                      ev.target.value = "";
                    }
                  }}
                  data-testid="gallery-upload-input"
                />
                <Button
                  type="button"
                  onClick={() => document.getElementById("gallery-upload-input")?.click()}
                  disabled={galleryUploading}
                  className="btn-primary rounded-lg px-3 py-1.5 text-xs"
                  data-testid="gallery-upload-btn"
                >
                  {galleryUploading
                    ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Hochladen ...</>
                    : <><Plus className="mr-1 h-3.5 w-3.5" /> Bild hochladen</>}
                </Button>
                <Button
                  type="button"
                  onClick={() => { setGalleryUrls(""); setGalleryOpen(true); }}
                  className="btn-ghost rounded-lg px-3 py-1.5 text-xs border border-white/10"
                  title="Bilder per URL hinzufuegen"
                  data-testid="gallery-add-url-btn"
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> URL einfuegen
                </Button>
              </div>
            </div>
            {(editing.gallery_images && editing.gallery_images.length > 0) ? (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="gallery-list">
                {editing.gallery_images.map((url, idx) => (
                  <div key={`${url}-${idx}`} className="group relative overflow-hidden rounded-md border border-white/10 bg-[#10141b]" data-testid={`gallery-item-${idx}`}>
                    <img
                      src={url}
                      alt={`Galeriebild ${idx + 1}`}
                      className="hb-pixel-img h-32 w-full object-cover"
                      onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                    />
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/85 to-transparent p-1.5 opacity-0 transition group-hover:opacity-100">
                      <span className="text-[10px] font-mono text-zinc-300 truncate flex-1" title={url}>{idx + 1}</span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={() => {
                            setEditing((prev) => {
                              if (!prev) return prev;
                              const arr = [...(prev.gallery_images || [])];
                              [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                              return { ...prev, gallery_images: arr };
                            });
                          }}
                          className="rounded bg-white/10 p-1 text-white hover:bg-white/20 disabled:opacity-30"
                          title="Nach oben"
                        >
                          <ChevronsLeft className="h-3 w-3 rotate-90" />
                        </button>
                        <button
                          type="button"
                          disabled={idx === editing.gallery_images.length - 1}
                          onClick={() => {
                            setEditing((prev) => {
                              if (!prev) return prev;
                              const arr = [...(prev.gallery_images || [])];
                              [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
                              return { ...prev, gallery_images: arr };
                            });
                          }}
                          className="rounded bg-white/10 p-1 text-white hover:bg-white/20 disabled:opacity-30"
                          title="Nach unten"
                        >
                          <ChevronsRight className="h-3 w-3 rotate-90" />
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            // Aus Artikel-Galerie entfernen
                            setEditing((prev) => prev ? ({ ...prev, gallery_images: (prev.gallery_images || []).filter((_, i) => i !== idx) }) : prev);
                            // Optional: aus Media-Library loeschen anbieten
                            if ((url || "").includes("/api/uploads/") && window.confirm("Bild auch dauerhaft aus der Medien-Galerie loeschen?\n(Andernfalls bleibt es in der Galerie und kann in anderen Artikeln verwendet werden.)")) {
                              try {
                                const name = url.split("/").pop();
                                await a.deleteUpload(name);
                                toast({ title: "Aus Galerie geloescht" });
                              } catch (err) {
                                const status = err?.response?.status;
                                const detail = err?.response?.data?.detail;
                                if (status === 409 && detail?.used_in) {
                                  const titles = detail.used_in.map((u) => `• ${u.title}`).join("\n");
                                  if (window.confirm(`Bild wird in ${detail.used_in.length} weiteren Artikel(n) verwendet:\n\n${titles}\n\nTrotzdem aus Galerie loeschen?`)) {
                                    try {
                                      const name = url.split("/").pop();
                                      await a.deleteUpload(name, true);
                                      toast({ title: "Trotzdem geloescht" });
                                    } catch (e2) {
                                      toast({ title: "Loeschen fehlgeschlagen", description: e2?.response?.data?.detail || String(e2), variant: "destructive" });
                                    }
                                  }
                                } else {
                                  toast({ title: "Loeschen fehlgeschlagen", description: typeof detail === "string" ? detail : String(err), variant: "destructive" });
                                }
                              }
                            }
                          }}
                          className="rounded bg-rose-500/30 p-1 text-rose-100 hover:bg-rose-500/50"
                          title="Aus Artikel entfernen"
                          data-testid={`gallery-remove-${idx}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-3 rounded-md border border-dashed border-white/10 p-6 text-center text-[11px] text-zinc-500">
                Noch keine Bilder. Lade welche hoch oder fuege URLs hinzu.
              </div>
            )}
          </div>

          {/* Inline-Image-Action-Popover (nur noch fuer Legacy-Artikel mit alten Inline-Bildern) */}
          {imgAction && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setImgAction(null)}
                data-testid="img-action-backdrop"
              />
              <div
                className="fixed z-50 flex items-center gap-1 rounded-lg border border-white/10 bg-[#0f141b] p-1.5 shadow-2xl"
                style={{
                  left: `${Math.max(8, imgAction.x - 140)}px`,
                  top: `${Math.max(8, imgAction.y - 50)}px`,
                }}
                data-testid="img-action-popover"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => { window.open(imgAction.src, "_blank", "noopener"); }}
                  className="rounded-md px-2 py-1.5 text-[11px] text-zinc-200 hover:bg-white/10"
                  title="In neuem Tab vergroessern"
                  data-testid="img-action-zoom"
                >
                  Vergroessern
                </button>
                <button
                  type="button"
                  disabled={imgActionBusy}
                  onClick={() => {
                    removeImageFromEditor(imgAction.el);
                    setImgAction(null);
                    toast({ title: "Aus Artikel entfernt", description: "Speichere den Artikel, um die Aenderung zu uebernehmen." });
                  }}
                  className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[11px] font-medium text-amber-300 hover:bg-amber-500/20"
                  title="Bild nur aus diesem Artikel entfernen (bleibt in Galerie)"
                  data-testid="img-action-remove-article"
                >
                  Aus Artikel entfernen
                </button>
                <button
                  type="button"
                  disabled={imgActionBusy || !imgAction.src.includes("/api/uploads/")}
                  onClick={async () => {
                    setImgActionBusy(true);
                    try {
                      const ok = await deleteFromMediaLibrary(imgAction.src, false);
                      if (ok) {
                        removeImageFromEditor(imgAction.el);
                        toast({ title: "Aus Galerie geloescht" });
                      }
                    } catch (err) {
                      toast({
                        title: "Loeschen fehlgeschlagen",
                        description: err?.response?.data?.detail || String(err),
                        variant: "destructive",
                      });
                    } finally {
                      setImgActionBusy(false);
                      setImgAction(null);
                    }
                  }}
                  className="rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-1.5 text-[11px] font-medium text-rose-300 hover:bg-rose-500/20 disabled:opacity-40"
                  title="Bild komplett aus der Galerie loeschen (kann nicht rueckgaengig gemacht werden)"
                  data-testid="img-action-delete-media"
                >
                  {imgActionBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Aus Galerie loeschen"}
                </button>
                <button
                  type="button"
                  onClick={() => setImgAction(null)}
                  className="rounded-md px-1.5 py-1.5 text-zinc-500 hover:bg-white/5"
                  title="Schliessen"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </>
          )}

          <div className="md:col-span-2 flex items-center gap-3 rounded-lg border border-white/10 bg-[#10141b] p-3">
            <Switch
              checked={editing.link_in_timeline}
              onCheckedChange={(v) => setEditing({ ...editing, link_in_timeline: v })}
            />
            <div className="text-sm text-zinc-300">
              <div className="font-semibold text-white">Automatisch in Zeitleiste anzeigen</div>
              <div className="text-xs text-zinc-500">
                Erzeugt eine Blase im Zeitstrahl (Größe je nach Kategorie: Jubiläum = groß, Rare/Event/Retro = mittel, News/Glitch/Community = klein).
              </div>
            </div>
          </div>

          {perms.news_sticky && (
            <div className="md:col-span-2 flex items-center gap-3 rounded-lg border border-white/10 bg-[#10141b] p-3">
              <Switch
                checked={!!editing.sticky}
                onCheckedChange={(v) => setEditing({ ...editing, sticky: v })}
              />
              <div className="text-sm text-zinc-300">
                <div className="font-semibold text-white flex items-center gap-2"><Pin className="h-3.5 w-3.5 text-[#ffb84d]" /> Sticky (immer oben)</div>
                <div className="text-xs text-zinc-500">Erscheint ganz oben in der Newsliste.</div>
              </div>
            </div>
          )}

          {!perms.news_review && !isFullAdmin && (
            <div className="md:col-span-2 rounded-lg border border-[#ffb84d]/30 bg-[#ffb84d]/10 p-3 text-xs text-[#ffb84d]">
              Hinweis: Dein Artikel wird zur Pruefung vorgelegt und nach Freigabe veroeffentlicht.
            </div>
          )}

          {/* Badges (z.B. Rare-Badge / Event-Badge) */}
          <div className="md:col-span-2 rounded-lg border border-white/10 bg-[#10141b] p-3">
            <div className="mb-2 text-sm font-semibold text-white">Badges zum Artikel</div>
            <div className="mb-3 text-xs text-zinc-500">
              Z.B. das Badge fuers Rare oder das Event-Badge. Name + Beschreibung werden automatisch aus den Habbo-Gamedata gezogen.
            </div>
            <BadgePicker
              value={editing.badges || []}
              onChange={(b) => setEditing({ ...editing, badges: b })}
              testIdPrefix="news-badges"
            />
          </div>
        </div>

        {/* Vorschau */}
        {editing.content_html && (
          <div className="mt-6 rounded-xl border border-white/10 bg-[#0f141b] p-5">
            <div className="text-xs uppercase tracking-wider text-[#9d7bff]">Vorschau</div>
            <div className="news-preview prose prose-invert mt-2 max-w-none news-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editing.content_html, {
                ADD_TAGS: ["table", "thead", "tbody", "tr", "td", "th", "hr", "div"],
                ADD_ATTR: ["target", "rel", "class", "style", "width", "height", "align", "colspan", "rowspan", "loading", "data-gallery"],
              }) }} />
          </div>
        )}
      </div>
    );
  }

  // Listen-Ansicht — schoenere Karten-Variante
  const statusTabs = [
    { key: "alle", label: "Alle", color: "text-zinc-200" },
    { key: "online", label: "Online", color: "text-emerald-300" },
    { key: "offline", label: "Offline", color: "text-zinc-400" },
    { key: "review", label: "Review", color: "text-[#ffb84d]" },
  ];

  return (
    <div>
      {/* Suche + Aktion */}
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Artikel suchen ..."
            className="pl-9 bg-[#0f141b] border-white/10 text-zinc-200"
            data-testid="news-search-input"
          />
        </div>
        <Button onClick={startNew} className="btn-primary rounded-lg px-4 py-2 text-sm font-semibold" data-testid="news-new-btn">
          <Plus className="mr-1 h-4 w-4" /> Neuer Artikel
        </Button>
      </div>

      {/* Status-Filter Tabs */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {statusTabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setFilterStatus(t.key)}
            data-testid={`news-filter-${t.key}`}
            className={`group inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
              filterStatus === t.key
                ? "border-[#7c4dff]/50 bg-[#7c4dff]/15 text-white shadow-[0_0_0_3px_rgba(124,77,255,0.1)]"
                : "border-white/10 bg-[#0f141b] text-zinc-400 hover:border-white/20 hover:text-zinc-200"
            }`}
          >
            <span className={t.color}>{t.label}</span>
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
              filterStatus === t.key ? "bg-white/15 text-white" : "bg-white/5 text-zinc-500"
            }`}>{counts[t.key]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="news-cards">
          {filtered.map((art) => {
            const isOffline = art.published === false && !art.needs_review;
            const isReview = !!art.needs_review;
            const isHot = hotTopicId === art.id;
            return (
              <div
                key={art.id}
                data-testid={`news-card-${art.id}`}
                className={`group relative overflow-hidden rounded-xl border bg-[#0f141b] transition hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#7c4dff]/10 ${
                  isHot ? "border-rose-500/50 ring-1 ring-rose-500/30" :
                  isReview ? "border-[#ffb84d]/40" :
                  isOffline ? "border-zinc-700/60 opacity-80" :
                  "border-white/10 hover:border-[#7c4dff]/40"
                }`}
              >
                {/* Header-Bild Cover */}
                <div className="relative h-32 w-full overflow-hidden bg-gradient-to-br from-[#1a1f29] to-[#10141b]">
                  {art.header_image ? (
                    <img
                      src={art.header_image}
                      alt=""
                      className="hb-pixel-img h-full w-full object-cover transition group-hover:scale-105"
                      onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-zinc-700 text-xs">
                      Kein Header-Bild
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f141b] via-transparent to-transparent" />

                  {/* Top-Left: Kategorie */}
                  <div className="absolute left-2 top-2 flex flex-wrap items-center gap-1.5">
                    <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${categoryColor(art.category)}`}>
                      {art.category}
                    </span>
                    {art.sticky && (
                      <span className="inline-flex items-center gap-0.5 rounded-md border border-[#ffb84d]/30 bg-[#ffb84d]/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#ffb84d]">
                        <Pin className="h-2.5 w-2.5" /> Sticky
                      </span>
                    )}
                  </div>

                  {/* Top-Right: Status-Pille */}
                  <div className="absolute right-2 top-2 flex items-center gap-1.5">
                    {isHot && (
                      <span className="inline-flex items-center gap-0.5 rounded-md border border-rose-500/40 bg-rose-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-200">
                        <Flame className="h-2.5 w-2.5" /> TOP
                      </span>
                    )}
                    {isReview ? (
                      <span className="inline-flex items-center gap-0.5 rounded-md border border-[#ffb84d]/40 bg-[#ffb84d]/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#ffb84d]">
                        <AlertCircle className="h-2.5 w-2.5" /> Review
                      </span>
                    ) : isOffline ? (
                      <span className="inline-flex items-center gap-0.5 rounded-md border border-zinc-500/30 bg-zinc-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-300">
                        <EyeOff className="h-2.5 w-2.5" /> Offline
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5 rounded-md border border-emerald-500/30 bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
                        <Eye className="h-2.5 w-2.5" /> Online
                      </span>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div className="space-y-2 p-3">
                  <h3 className="line-clamp-2 text-sm font-bold leading-tight text-white">
                    {art.title}
                  </h3>
                  {art.excerpt && (
                    <p className="line-clamp-2 text-xs text-zinc-400 leading-snug">
                      {art.excerpt}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
                    <span className="inline-flex items-center gap-1">
                      <UserIcon className="h-3 w-3" /> {art.author}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <CalendarIcon className="h-3 w-3" /> {art.date}{art.time ? ` · ${art.time}` : ""}
                    </span>
                  </div>
                  <div className="truncate text-[10px] font-mono text-zinc-600">/{art.id}</div>
                </div>

                {/* Aktionen */}
                <div className="flex flex-wrap items-center gap-1 border-t border-white/5 bg-[#0a0d12] px-2 py-2">
                  <Button
                    onClick={() => startEdit(art)}
                    className="btn-ghost rounded-md px-2 py-1 text-[11px] font-medium"
                    data-testid={`edit-${art.id}`}
                  >
                    <Edit3 className="mr-1 h-3 w-3" /> Bearbeiten
                  </Button>
                  {perms.news_write && !art.needs_review && (
                    <Button
                      onClick={() => onTogglePublish(art)}
                      data-testid={`toggle-publish-${art.id}`}
                      className={`btn-ghost rounded-md px-2 py-1 text-[11px] font-medium ${isOffline ? "text-emerald-300 hover:border-emerald-500/40" : "text-zinc-300 hover:border-zinc-500/40"}`}
                      title={isOffline ? "Online stellen" : "Offline nehmen"}
                    >
                      {isOffline
                        ? <><Eye className="mr-1 h-3 w-3" /> Online</>
                        : <><EyeOff className="mr-1 h-3 w-3" /> Offline</>}
                    </Button>
                  )}
                  {perms.news_review && art.needs_review && (
                    <Button onClick={() => onApprove(art)} data-testid={`approve-${art.id}`} className="btn-primary rounded-md px-2 py-1 text-[11px] font-semibold">
                      <CheckSquare className="mr-1 h-3 w-3" /> Freigeben
                    </Button>
                  )}
                  <div className="ml-auto flex items-center gap-1">
                    {perms.news_sticky && (
                      <Button
                        onClick={() => onToggleSticky(art)}
                        data-testid={`sticky-${art.id}`}
                        className="btn-ghost rounded-md p-1.5"
                        title={art.sticky ? "Sticky entfernen" : "Sticky setzen"}
                      >
                        <Pin className={`h-3.5 w-3.5 ${art.sticky ? "text-[#ffb84d]" : "text-zinc-400"}`} />
                      </Button>
                    )}
                    {isFullAdmin && (
                      <Button
                        onClick={() => onSetHotTopic(art)}
                        data-testid={`hot-topic-${art.id}`}
                        className="btn-ghost rounded-md p-1.5"
                        title={isHot ? "TOP News entfernen" : "Als TOP News setzen"}
                      >
                        <Flame className={`h-3.5 w-3.5 ${isHot ? "text-rose-400" : "text-zinc-400"}`} />
                      </Button>
                    )}
                    <Button
                      onClick={() => onDelete(art)}
                      data-testid={`delete-${art.id}`}
                      className="btn-ghost rounded-md p-1.5 text-rose-300 hover:border-rose-500/40"
                      title="Loeschen"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full rounded-xl border border-dashed border-white/10 bg-[#0f141b] py-12 text-center text-sm text-zinc-500">
              Keine Artikel gefunden.
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default NewsPanel;
